import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import DropdownMenuCheckboxItem from '@/components/DropdownMenu/DropdownMenuCheckboxItem';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useCollectionStore } from '@/store';

const DropdownMenuFilter = ({ 
  labels, 
  currentLabelPath, 
  onLabelClick,
  onActionClick,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [isActionsSubOpen, setIsActionsSubOpen] = React.useState(false);
  // Collections for actions sub-menu (e.g., Add to collection)
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const collections = useMemo(() => {
    return (collectionIds || []).map(id => collectionsMap[id]).filter(Boolean);
  }, [collectionsMap, collectionIds]);

  return (
    <DropdownMenu.Content 
      className="DropdownMenuContent" 
      align="end" 
      sideOffset={5}
      accessibilityRole="menu"
      accessibilityLabel="Filter options menu"
    >
      {labels.map((section, sectionIndex) => (
        <React.Fragment key={`dropdown-menu-filter-section-${sectionIndex}`}>
          {/* Section headings have no equivalent in a native platform menu,
              which only renders items, so they stay web-only. */}
          {section.title && Platform.OS === 'web' && (
            <DropdownMenuLabel text={section.title} />
          )}
          
          {section.id === 'actions' ? (
            section.children.map((option, index) => {
              if (option.id === 'add-to-collection') {
                return (
                  <DropdownMenu.Sub 
                    key={`dropdown-menu-action-${index}`}
                    open={isActionsSubOpen}
                    onOpenChange={setIsActionsSubOpen}
                  >
                    <DropdownMenu.SubTrigger className="DropdownMenuItem">
                      <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                        {option.title}
                      </DropdownMenu.ItemTitle>
                      <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                        <Icon name="chevron-right" size={12} />
                      </DropdownMenu.ItemIcon>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent
                      className="DropdownMenuSubContent"
                      side="right"
                      align="start"
                      sideOffset={4}
                    >
                      {isXS && (
                        <>
                          <DropdownMenu.Item
                            key="actions-back"
                            className="DropdownMenuItem"
                            onSelect={(event) => {
                              if (event?.preventDefault) {
                                event.preventDefault();
                              }
                              setIsActionsSubOpen(false);
                            }}
                          >
                            <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                              <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                            </DropdownMenu.ItemIcon>
                            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                              Back
                            </DropdownMenu.ItemTitle>
                          </DropdownMenu.Item>
                          <DropdownMenu.Separator className="DropdownMenuSeparator" />
                        </>
                      )}
                      {collections.length > 0 ? (
                        collections.map((collection) => (
                          <DropdownMenu.Item
                            key={`dropdown-menu-collection-${collection.id}`}
                            className="DropdownMenuItem"
                            onSelect={() => onActionClick?.(option.id, { collectionId: collection.id })}
                          >
                            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                              {collection.title}
                            </DropdownMenu.ItemTitle>
                          </DropdownMenu.Item>
                        ))
                      ) : (
                        <DropdownMenu.Item key="no-collections" className="DropdownMenuItem" disabled>
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">No collections</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                      )}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Sub>
                );
              }
              return (
                <DropdownMenu.Item 
                  key={`dropdown-menu-action-${index}`}
                  className="DropdownMenuItem"
                  onSelect={() => onActionClick?.(option.id)}
                  role="menuitem"
                >
                  <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">{option.title}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              );
            })
          ) : (
            section.children.map((option, index) => {
              const isActive = currentLabelPath.some(l => l.id === option.id);

              // The native menu only reads children whose element type is
              // DropdownMenu.Item, so the checkbox wrapper is web-only.
              if (Platform.OS !== 'web') {
                return (
                  <DropdownMenu.Item
                    key={`dropdown-menu-filter-option-${index}`}
                    onSelect={() => onLabelClick(option.id)}
                  >
                    {isActive && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
                    <DropdownMenu.ItemTitle>{option.title}</DropdownMenu.ItemTitle>
                  </DropdownMenu.Item>
                );
              }

              return (
                <DropdownMenuCheckboxItem
                  key={`dropdown-menu-filter-option-${index}`}
                  label={option.title}
                  isActive={isActive}
                  onValueChange={() => onLabelClick(option.id)}
                />
              );
            })
          )}
          
          {sectionIndex < labels.length - 1 && (
            <DropdownMenu.Separator 
              className="DropdownMenuSeparator"
            />
          )}
        </React.Fragment>
      ))}
    </DropdownMenu.Content>
  );
};

DropdownMenuFilter.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  currentLabelPath: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  onLabelClick: PropTypes.func.isRequired,
  onActionClick: PropTypes.func,
};

DropdownMenuFilter.displayName = 'DropdownMenuFilter';

export default React.memo(DropdownMenuFilter); 